import { signedExtraAmount } from '../utils/extraSign';

export function calculateKPIs({
  constructionCosts,
  generalCosts,
  adminCosts,
  extras,
  extrasCosts
}) {
  // extrasCosts is the financial dataset (commitment, expenses, forecast)
  // extras is used only for status and contract value

  const allCosts = [...constructionCosts, ...generalCosts, ...adminCosts];

  const totalContract =
    allCosts?.reduce((sum, item) => sum + (item?.budget ?? 0), 0) ?? 0;

  // CONTRACT SIDE — use extras for status and accepted amounts
  const approvedExtrasSum =
    extras
      ?.filter(e => e?.type === 'extra' && e?.status === 'approved')
      ?.reduce((sum, e) => sum + (parseFloat(e?.costAccepted ?? e?.amount) || 0), 0) || 0;

  const approvedCreditsSum =
    extras
      ?.filter(e => e?.type === 'credit' && e?.status === 'approved')
      ?.reduce((sum, e) => sum + (parseFloat(e?.costAccepted ?? e?.amount) || 0), 0) || 0;

  const netChange = approvedExtrasSum - approvedCreditsSum;

  // Build approved IDs from extras (status lives in extras)
  const approvedExtraIds = extras
    ?.filter(e => e?.status === 'approved')
    ?.map(e => e?.id) || [];

  // effective cost uses the higher of actual cost vs forecast
  // this allows KPI cards to reflect projected final cost
  const existingCostCodesTotal =
    allCosts?.reduce((sum, item) => {
      const actualCost = (item?.commitmentActual ?? 0) + (item?.expensesActual ?? 0);
      // projected cost = actual committed/spent + forecast reserve
      const effectiveCost = actualCost + (item?.forecast ?? 0);
      return sum + effectiveCost;
    }, 0) ?? 0;

  // COST SIDE — use extrasCosts for financial values (commitment, expenses, forecast)
  // include forecast from approved extras as additional projected cost (risk reserve)
  const extrasCostTotal =
    extrasCosts
      ?.filter(e => approvedExtraIds?.includes(e?.id))
      ?.reduce((sum, e) => {
        const commitment = parseFloat(e?.commitment) || 0;
        const expenses = parseFloat(e?.expenses) || 0;
        const forecast = parseFloat(e?.forecast) || 0;
        return sum + commitment + expenses + forecast;
      }, 0) || 0;

  const totalActualCost = existingCostCodesTotal + extrasCostTotal;

  const adjustedContract = totalContract + netChange;
  const liveProfit = adjustedContract - totalActualCost;
  const profitMargin =
    adjustedContract > 0 ? (liveProfit / adjustedContract) * 100 : 0;

  // Extras Status Summary — all calculations live here, not in UI
  //
  // Le signe vient du TYPE (voir src/utils/extraSign.js). Sans lui, les quatre
  // tuiles de Directives & Extras ADDITIONNAIENT les credits au lieu de les
  // soustraire : sur P25-017, « Approuve » affichait 5 846,84 $ au lieu de
  // 1 846,84 $.
  const getSubmitted = (e) => signedExtraAmount(e, e?.submittedCost || e?.amount);
  const getAccepted = (e) => signedExtraAmount(e, e?.costAccepted);

  const submittedItems = extras?.filter(e => e?.status === 'submitted') || [];
  const approvedItems = extras?.filter(e => e?.status === 'approved') || [];
  const rejectedItems = extras?.filter(e => e?.status === 'rejected') || [];

  // Les deux tuiles du milieu ventilent les APPROUVES par type. Elles partent
  // du meme ensemble que la tuile Total (approvedItems) : leurs comptes
  // s'additionnent donc exactement a celui du Total, et leurs montants aussi.
  const approvedExtraItems = approvedItems?.filter(e => e?.type === 'extra') || [];
  const approvedCreditItems = approvedItems?.filter(e => e?.type === 'credit') || [];

  const statusSummary = {
    submitted: {
      count: submittedItems?.length,
      amount: submittedItems?.reduce((sum, e) => sum + getSubmitted(e), 0)
    },
    // `approved` alimente la tuile « Total ». Sa somme n'a PAS change : c'est
    // elle qui doit rester egale a la tuile « Extra/Credit » de l'Apercu
    // financier (netChange). Ne jamais la redefinir comme extra + credit.
    approved: {
      count: approvedItems?.length,
      amount: approvedItems?.reduce((sum, e) => sum + getAccepted(e), 0)
    },
    extra: {
      count: approvedExtraItems?.length,
      amount: approvedExtraItems?.reduce((sum, e) => sum + getAccepted(e), 0)
    },
    credit: {
      count: approvedCreditItems?.length,
      amount: approvedCreditItems?.reduce((sum, e) => sum + getAccepted(e), 0)
    },
    rejected: {
      count: rejectedItems?.length,
      amount: rejectedItems?.reduce((sum, e) => sum + getSubmitted(e), 0)
    }
  };

  return {
    totalContract,
    netChange,
    adjustedContract,
    totalActualCost,
    liveProfit,
    profitMargin,
    statusSummary
  };
}
